import { useContext, useEffect } from 'react';
import { differenceInDays } from 'date-fns';
import { graphql } from 'react-relay';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ONBOARDING_PERSONALIZED_TEMPLATES_V2 } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useUserByLicensedProducts } from '@confluence/onboarding-helpers/entry-points/hooks/useUserByLicensedProducts.ts';
import { useIsEstablishedUserTraitName } from '@confluence/onboarding-helpers/entry-points/hooks/useIsEstablishedUserTraitName.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useAtlassianAccountTraits } from '@confluence/targeting/entry-points/useAtlassianAccountTraits.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useIsEligibleForPersonalizedTemplatesV2GetPersonalSpaceKeyQuery$data,
	useIsEligibleForPersonalizedTemplatesV2GetPersonalSpaceKeyQuery$variables,
} from './__generated__/useIsEligibleForPersonalizedTemplatesV2GetPersonalSpaceKeyQuery.graphql.ts';

export const GetPersonalSpaceKeyQuery = graphql`
	query useIsEligibleForPersonalizedTemplatesV2GetPersonalSpaceKeyQuery {
		user: confluence_atlassianUser(current: true) {
			id
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

export const useIsEligibleForPersonalizedTemplatesV2 = () => {
	const { userId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();
	useEffect(() => {
		experienceTracker.start({
			name: ONBOARDING_PERSONALIZED_TEMPLATES_V2,
		});
	}, [experienceTracker]);

	// First check if they are even a founding admin
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...Object.values(PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS),
		],
		!isSiteAdmin,
	);

	const {
		data: personalSpaceKeyData,
		loading: personalSpaceKeyLoading,
		error: personalSpaceKeyError,
	} = useQueryInterop<
		useIsEligibleForPersonalizedTemplatesV2GetPersonalSpaceKeyQuery$data,
		useIsEligibleForPersonalizedTemplatesV2GetPersonalSpaceKeyQuery$variables
	>(GetPersonalSpaceKeyQuery, {
		skip: !userId,
	});

	const {
		data: accountTraitsData,
		loading: accountTraitsLoading,
		error: accountTraitsError,
	} = useAtlassianAccountTraits({
		skipBootstrap: !isSiteAdmin,
	});

	const {
		isFoundingAdmin,
		takeoverSeenAt,
		hasDismissedOnboardingActions,
		isTemplateListDismissed,
	} = deserializeState(onboardingStateData);

	const personalSpaceKey =
		(personalSpaceKeyData?.user?.confluence?.hasPersonalSpace &&
			personalSpaceKeyData?.user?.confluence?.space?.key) ||
		'';

	const hasDismissedTakeover = hasDismissedOnboardingActions && isTemplateListDismissed;

	const isInOnboardingPeriod =
		Boolean(takeoverSeenAt) && differenceInDays(new Date(), new Date(takeoverSeenAt)) <= 14;
	const takeoverExpired =
		Boolean(takeoverSeenAt) && differenceInDays(new Date(), new Date(takeoverSeenAt)) > 14;

	const isEstablishedUser =
		accountTraitsData?.attributes?.find((attr) => attr?.name === isEstablishedUserTraitName)
			?.value === 'true';

	const potentiallyEligibleUserForPTV2 =
		isFoundingAdmin && !takeoverExpired && !hasDismissedTakeover && !isEstablishedUser;
	// If both founding admin + !established user, then we can check if they are a land founding admin

	const {
		isLandUser,
		isXflowUser,
		error: userTypeError,
		loading: userTypeLoading,
	} = useUserByLicensedProducts({
		skip: !potentiallyEligibleUserForPTV2,
	});

	/**
	 * 1. If they have seen it, then they are simply eligible for the takeover.
	 * 2. If they haven't seen it yet,  then we need to check the other eligibility conditions
	 * 		to determine if we should set `takeoverSeenAt` later -> show the takeover.
	 *   a. They need to be not established
	 *   b. They need to be a founding admin
	 *   c. They need to have a personal space
	 */
	const isGenerallyEligible =
		takeoverSeenAt !== undefined ||
		Boolean(isFoundingAdmin && !isEstablishedUser && Boolean(personalSpaceKey));

	if (
		personalSpaceKeyLoading ||
		userTypeLoading ||
		onboardingStateLoading ||
		accountTraitsLoading
	) {
		return {
			isEligibleForLand: false,
			isEligibleForXflow: false,
			loading: true,
			isVisible: false,
		};
	}

	if (userTypeError || onboardingStateError || personalSpaceKeyError || accountTraitsError) {
		experienceTracker.stopOnError({
			name: ONBOARDING_PERSONALIZED_TEMPLATES_V2,
			error: (userTypeError ||
				onboardingStateError ||
				personalSpaceKeyError ||
				accountTraitsError)!,
		});
	}

	return {
		isEligibleForLand: isLandUser && isGenerallyEligible,
		isEligibleForXflow: isXflowUser && isGenerallyEligible,
		loading: false,
		isVisible:
			(isLandUser || isXflowUser) &&
			isGenerallyEligible &&
			(takeoverSeenAt === undefined || isInOnboardingPeriod) &&
			!hasDismissedTakeover,
		wasPTV2TakeoverSeen: Boolean(takeoverSeenAt),
	};
};
